import { type ClassValue, clsx } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

/**
 * tailwind-merge configuré pour les tokens Cinqo.
 *
 * Le preset @cinqo/brand définit des fontSize custom (`text-label`, `text-caption`,
 * `text-body`…) ET des couleurs custom employées comme text color (`text-info`,
 * `text-success`…). Sans config, tailwind-merge range les deux dans le même groupe
 * `text-*` et n'en conserve qu'un : `cn('text-info', 'text-label')` supprime
 * silencieusement `text-info` (bug StatusBadge — le badge perd sa couleur d'intent).
 * On déclare donc les fontSize custom dans le groupe `font-size` pour que couleur
 * et taille cohabitent au lieu de s'exclure.
 *
 * Règle : tout token fontSize ajouté au preset (packages/brand/tailwind-preset.js)
 * doit être répercuté ici, sinon la collision réapparaît.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [{ text: ['h1', 'h2', 'h3', 'body', 'data', 'caption', 'label'] }],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Applique une opacité à une couleur hex en la convertissant en rgba().
 *
 * Remplace le pattern DIY `color + '40'` qui concatenait un suffixe alpha hex —
 * fragile (marche seulement si hex est #RRGGBB), illisible (0x40 → 25% ?), et
 * invalide si la couleur n'est pas déjà sous forme #RRGGBB.
 *
 * @param hex Couleur hex (#RGB, #RGBA, #RRGGBB ou #RRGGBBAA — avec ou sans #).
 *            Si null/undefined ou format invalide, retourne `transparent`.
 * @param alpha Opacité entre 0 et 1 (clampée).
 * @returns Chaîne `rgba(r, g, b, a)` utilisable dans un style CSS.
 *
 * @example
 *   withAlpha('#7C3AED', 0.25)  // 'rgba(124, 58, 237, 0.25)'
 *   withAlpha('#fff', 0.5)       // 'rgba(255, 255, 255, 0.5)'
 */
export function withAlpha(hex: string | null | undefined, alpha: number): string {
  if (!hex) return 'transparent'
  let h = hex.startsWith('#') ? hex.slice(1) : hex
  // #RGB → #RRGGBB
  if (h.length === 3) h = h.split('').map((c) => c + c).join('')
  // #RGBA → on ignore l'alpha existant
  else if (h.length === 4) h = h.slice(0, 3).split('').map((c) => c + c).join('')
  // #RRGGBBAA → on ignore l'alpha existant
  else if (h.length === 8) h = h.slice(0, 6)
  if (h.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(h)) return 'transparent'
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  const a = Math.max(0, Math.min(1, alpha))
  return `rgba(${r}, ${g}, ${b}, ${a})`
}
